import { inject, injectable, interfaces } from 'inversify';
import { debounce } from 'lodash';
import * as path from 'path';
import { InputStatus } from '../../../__legacy__/models/error.model';
import { IAttachmentPreviewImageInfo } from '../../../__legacy__/modules/attachments/attachmentsPreview.store';
import { ReservedFileNames } from '../../../__legacy__/modules/datasets/datasetDetails.service';
import { NotificationLevel, ToastNotification } from '../../../__legacy__/modules/notifications/models/notification.model';
import { INotificationsService } from '../../../__legacy__/modules/notifications/services/notifications.service';
import { CacheManager } from '../../../__legacy__/services/cacheManager';
import { IEventBus } from '../../../__legacy__/services/eventBus.service';
import { ICurrentWorkspaceStore } from '../../workspaces/currentWorkspace/CurrentWorkspace.store';
import { AttachmentRenamedEvent } from './events/AttachmentRenamedEvent';
import { IRenameAttachmentStore } from './RenameAttachment.store';
import { ICheckAttachmentNameUniquenessRequest, ICheckAttachmentNameUniquenessService } from './services/CheckAttachmentNameUniqueness.service';
import { IRenameAttachmentRequest, IRenameAttachmentService } from './services/RenameAttachment.service';

export interface IRenameAttachmentBl {
  renameAttachmentAsync(): Promise<void>;
  changeName(name: string): void;
  showModal(): void;
  hideModal(): void;
  initialize(attachmentId: string, attachmentName: string, parentId: string | undefined): void;
}

export namespace IRenameAttachmentBl {
  export const $: interfaces.ServiceIdentifier<IRenameAttachmentBl> = Symbol('RENAME_ATTACHMENT_BL');
}

@injectable()
export class RenameAttachmentBl implements IRenameAttachmentBl {
  constructor(
    @inject(IRenameAttachmentStore.$) private readonly store: IRenameAttachmentStore,
    @inject(ICheckAttachmentNameUniquenessService.$) private readonly checkUniquenessService: ICheckAttachmentNameUniquenessService,
    @inject(IRenameAttachmentService.$) private readonly renameAttachmentService: IRenameAttachmentService,
    @inject(INotificationsService.$) private readonly notificationService: INotificationsService,
    @inject(ICurrentWorkspaceStore.$) private readonly currentWorkspaceStore: ICurrentWorkspaceStore,
    @inject(IEventBus.$) private readonly eventBus: IEventBus
  ) {}

  hideModal(): void {
    this.store.setShowModal(false);
  }

  showModal(): void {
    this.store.setShowModal(true);
  }

  initialize(attachmentId: string, attachmentName: string, parentId: string | undefined): void {
    this.store.initialize(attachmentId, attachmentName, parentId);
  }

  public changeName(name: string): void {
    this.store.setName(name);
    void this.validateNameDebounced(name);
  }

  async renameAttachmentAsync(): Promise<void> {
    await this.validateNameAsync(this.store.name);
    if (!this.store.nameStatus.isValid) {
      return;
    }

    try {
      const request: IRenameAttachmentRequest = {
        name: this.store.name,
        attachmentId: this.store.id,
      };
      await this.renameAttachmentService.renameAttachmentAsync(request);
      this.eventBus.sendEvent(new AttachmentRenamedEvent());

      await this.updateCacheAsync();
    } catch {
      await this.notificationService.push(new ToastNotification(NotificationLevel.ERROR, 'renaming_attachment_failed'));
    }

    this.hideModal();
  }

  private async updateCacheAsync(): Promise<void> {
    const cacheAvailable = await CacheManager.checkCacheAvailabilityAsync();
    if (!cacheAvailable) {
      return;
    }

    const request = new Request(`/home/${this.currentWorkspaceStore.currentWorkspace.id}/attachments/${this.store.id}/preview/${this.store.id}`);

    const imagePreviewCache = await caches.open('image-preview-cache');
    const imagePreviewMatch = await imagePreviewCache.match(request);

    if (imagePreviewMatch) {
      const cachedPreviewImageData = await imagePreviewMatch.json();
      const result = {} as IAttachmentPreviewImageInfo;
      Object.assign(result, cachedPreviewImageData);
      result.name = this.store.name;
      await imagePreviewCache.put(request, new Response(JSON.stringify(result)));
    }
  }

  private async validateNameAsync(name: string): Promise<void> {
    const nameStatus = InputStatus.valid();

    if (name.trim() === '') {
      nameStatus.addErrorCode('field_cant_be_empty');
    }

    const format = /[*:"\\|<>\/?]+/;
    if (format.test(name)) {
      nameStatus.addErrorCode('no_special_characters');
    }

    const baseName = path.basename(name, path.extname(name));
    if (ReservedFileNames.some((fn) => baseName.toUpperCase() === fn)) {
      nameStatus.addErrorCode('attachments.validation.forbidden_attachment_name');
    }
    if (/^\.+$/.test(name)) {
      nameStatus.addErrorCode('attachments.validation.forbidden_attachment_name');
    }

    const request: ICheckAttachmentNameUniquenessRequest = {
      name,
      attachmentId: this.store.id,
      workspaceId: this.currentWorkspaceStore.currentWorkspace.id,
      parentId: this.store.parentId,
    };

    if (!(await this.checkUniquenessService.checkAttachmentNameUniqueness(request))) {
      nameStatus.addErrorCode('attachment_name_must_be_unique');
    }

    this.store.setNameStatus(nameStatus);
  }

  private validateNameDebounced = debounce(async (name: string) => {
    await this.validateNameAsync(name);
  }, 300);
}
